import type { FC } from 'react';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { Subscribe } from 'unstated';
import { useQuery } from '@apollo/react-hooks';
import throttle from 'lodash/throttle';

import { Box, Flex, xcss } from '@atlaskit/primitives';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';

import {
	EditorContentStateQuery,
	type EditorContentStateQueryType,
	type EditorContentStateQueryVariables,
	ContentStateStateContainer,
	EditorContentState,
	RendererContentState,
} from '@confluence/content-state';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { isExternalShareRequest } from '@confluence/external-share-utils';
import { fg } from '@confluence/feature-gating';
import { PageOwnershipByline, BadgeStylePageOwnershipByline } from '@confluence/content-ownership';
import { ProfileAvatar, ProfileLink } from '@confluence/profile';
import { useSpaceId } from '@confluence/space-utils';
import { LivePagesByLineReactions } from '@confluence/live-pages-features/entry-points/LivePagesByLineReactions';
import { AnalyticsByLine } from '@confluence/confluence-analytics';
import { ReadTime } from '@confluence/read-time';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';

import { useByLineApps } from './useByLineApps';
import { LazyLivePagesByLineApps } from './LivePagesByLineApps';
import { png_1x1_n40 } from './png1x1';
import {
	BylineStyledTextDiv,
	StyledAuthorDiv,
	StyledAvatars,
	LivePagesBylineStyledMainDiv,
	LivePagesBylineStyledMainDivSingleLine,
	StyledNameSkeleton,
} from './PresentationalComponents';
import type { UserShape, FullPageEditorPresetAPI, Version } from './byLineTypes';
import { getArrangedContributors } from './byLineUtils';
import { LivePagesByLineLastUpdated } from './LivePagesByLineLastUpdated';
import { BylineClassification } from './BylineClassification';

export type LivePagesByLineWithContributorProps = {
	contentId: string;
	author: UserShape;
	owner: UserShape | null;
	lastOwner: UserShape | null;
	createdDate: Date | null;
	contributors: UserShape[];
	contributorsCount: number;
	lastModifyUser: UserShape;
	contentType: string;
	isPublishingContent: boolean;
	userOffline: boolean;
	isKnowledgeBaseSpace: boolean;
	version: Version;
	viewMode?: boolean;
	editorApi?: FullPageEditorPresetAPI | null;
	isUnpublishedDraft?: boolean;
};

type userAvatarShape = {
	isAnonymous: boolean;
	userId: string;
	src: string;
	name: string;
};

const firstElementStyles = xcss({
	marginLeft: 'space.150',
});

const classificationStyles = xcss({
	marginTop: 'space.025',
});

const i18n = defineMessages({
	showAllApps: {
		id: 'content-header.livepages.by-line.showAllApps',
		defaultMessage: 'Show all apps',
		description: 'Label for an icon button to show all hidden apps',
	},
});

const contentStateStyles = xcss({
	marginTop: 'space.025',
});

const pageViewsComponentStyles = xcss({
	marginTop: 'space.050',
});

const textContainerStyles = xcss({
	margin: 'space.0',
	/* IE11 */
	width: '100%',
});

const bylineAppsStyles = xcss({
	marginTop: 'space.100',
	font: 'font.body.small',
	display: 'inline',
});

const singleLineBaseStyles = xcss({
	marginTop: 'space.025',
	marginLeft: 'space.0',
	whiteSpace: 'nowrap',
	overflowX: 'hidden',
});

const singleLineWrapStyles = xcss({
	flexWrap: 'wrap',
	overflowX: 'visible',
});

const toggleButtonGradientStyles = xcss({
	height: '100%',
	position: 'relative',
	'::before': {
		content: '""',
		background: `linear-gradient(to right, transparent, ${token('elevation.surface')})`,
		height: '100%',
		left: 'space.negative.400',
		position: 'absolute',
		width: '32px',
	},
});

const floatingToolbarButtonStyle = { height: '24px', alignItems: 'center' };

export const LivePagesByLineWithContributorsComponent: FC<LivePagesByLineWithContributorProps> =
	memo(
		({
			contentId,
			author,
			owner,
			createdDate,
			lastModifyUser,
			contributors,
			contributorsCount,
			lastOwner,
			contentType,
			isPublishingContent,
			userOffline,
			isKnowledgeBaseSpace,
			viewMode,
			editorApi,
			isUnpublishedDraft,
		}) => {
			const [showAllApps, setShowAllApps] = useState(false);
			const [currentParentWidth, setCurrentParentWidth] = useState<number | undefined>();
			const [isOverflowing, setIsOverflowing] = useState(false);
			const isLivePage = useIsCurrentPageLive();
			const isExternalShare = isExternalShareRequest();
			const intl = useIntl();
			const showLessButtonRef = useRef<HTMLDivElement | null>(null);
			const bylineRef = useRef<HTMLDivElement | null>(null);

			const { data } = useQuery<EditorContentStateQueryType, EditorContentStateQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
				EditorContentStateQuery,
				{
					fetchPolicy: 'cache-first',
					variables: {
						contentId,
						isDraft: true,
					},
					skip: !Boolean(window.__SSR_RENDERED__),
				},
			);

			const { hasApps, connectItems, forgeItems, forgeError } = useByLineApps(contentId);

			useEffect(() => {
				const checkOverflow = () => {
					const bylineWidth = bylineRef?.current?.scrollWidth || 0;
					const showLessButtonOffsetTop = showLessButtonRef?.current?.offsetTop || 0;
					// currentParentWidth is undefined initially so we can default to editor content area to begin until the width observer kicks in
					const contentAreaWidth =
						currentParentWidth || document.querySelector('.ak-editor-content-area')?.clientWidth;

					if (bylineWidth && contentAreaWidth) {
						setIsOverflowing(bylineWidth > contentAreaWidth);

						if (contentAreaWidth >= bylineWidth) {
							// we dont want to show the show less button if we are already showing all apps and are in a single row
							// offsetTop should be close to 1 if its on the same row as its flex container
							if (showLessButtonOffsetTop <= 15) {
								setShowAllApps(false);
							}
						}
					}
				};

				checkOverflow();
			}, [currentParentWidth]);

			const spaceId = useSpaceId();
			const toggleShowAllApps = () => {
				setShowAllApps((currentState) => !currentState);
			};

			const updateContentAreaWidth = throttle((width: number) => {
				setCurrentParentWidth(width);
			}, 100);

			const getContributorsData = useMemo(() => {
				const arrangedContributors: UserShape[] = getArrangedContributors({
					owner,
					author,
					lastModifier: lastModifyUser,
					contributors,
				});

				const userToAvatarGroupDataItem: (user) => userAvatarShape = (user) => ({
					// Props for <ProfileAvatar>
					isAnonymous: user.isAnonymous,
					userId: user.userId,
					src: user.avatarUrl,
					// Extra props
					name: user.fullName,
				});

				return [
					...arrangedContributors
						// Actual data for the users we know about
						.map(userToAvatarGroupDataItem),
					// Skeletons for the ones we don't
					...Array(Math.max(contributorsCount - arrangedContributors.length, 0))
						.fill(null)
						.map(() => ({
							src: png_1x1_n40,
							name: <StyledNameSkeleton />,
						})),
				];
			}, [owner, author, lastModifyUser, contributors, contributorsCount]);

			const pageOwnerOrFirstContributorData = getContributorsData[0] as userAvatarShape;

			const CreatorOrOwnerByline = useMemo(() => {
				if (owner !== null) {
					return (
						<PageOwnershipByline
							owner={owner}
							creator={author}
							createdDate={createdDate}
							templateId={null}
							contentId={contentId}
							lastOwner={lastOwner}
						/>
					);
				}

				return (
					<FormattedMessage
						id="content-header.livepages.by-line.by.expanded"
						defaultMessage="By {author}"
						description="Text to show author details"
						values={{
							author: (
								<ProfileLink
									userId={author.userId}
									fullName={author.fullName}
									isAnonymous={author.isAnonymous}
									referralSource="profilecard"
								/>
							),
						}}
					/>
				);
			}, [author, owner, createdDate, contentId, lastOwner]);

			const ByLineCommonContent = (
				<>
					<Box xcss={firstElementStyles} />
					{fg('cc_page_experiences_byline_classification') && (
						<Box xcss={classificationStyles}>
							<BylineClassification contentId={contentId} />
						</Box>
					)}
					<Box xcss={!fg('cc_page_experiences_byline_badge_style') && contentStateStyles}>
						<Subscribe to={[ContentStateStateContainer]}>
							{(contentStateState: ContentStateStateContainer) => {
								// view mode users should not be able to edit the content state, so we just render the content state.
								if (viewMode) {
									return (
										<RendererContentState
											contentId={contentId}
											hasSeparator={false}
											isPagePreview // this sets the query to use draft version over published version
										/>
									);
								}
								// Knowledge space live pages will always show the editable content state component.
								// Other live pages will only show the editable content state component if the content state is already set.
								// This is because a page without a content state is initialized by the ContentStateHoverButton component.
								// SSRd content state will have it's value in gql cache initially instead of the unstated store
								const isSSRRenderedOnLoad =
									Boolean(data?.singleContent?.contentState) &&
									Boolean(window.__SSR_RENDERED__) &&
									contentStateState.state.shouldRunContentStateEffect;
								if (
									contentStateState.state.contentState ||
									isKnowledgeBaseSpace ||
									isSSRRenderedOnLoad
								) {
									return (
										<EditorContentState
											contentId={contentId}
											contentType={contentType}
											isPublishingContent={isPublishingContent}
											userOffline={userOffline}
											floatingToolbarButtonStyle={floatingToolbarButtonStyle}
										/>
									);
								} else {
									return null;
								}
							}}
						</Subscribe>
					</Box>
					{
						// eslint-disable-next-line confluence-feature-gating/no-preconditioning
						isLivePage &&
							fg('live_pages_byline_last_updated') &&
							!fg('confluence_frontend_object_header') && (
								<LivePagesByLineLastUpdated contentId={contentId} editorApi={editorApi} />
							)
					}
					{fg('live_pages_byline_read_time') && (
						<ReadTime contentId={contentId} editorApi={editorApi} isInEditor />
					)}
					{(isLivePage || !isUnpublishedDraft) && (
						<Box xcss={!fg('cc_page_experiences_byline_badge_style') && pageViewsComponentStyles}>
							<AnalyticsByLine contentId={contentId} />
						</Box>
					)}
					{(isLivePage ||
						(!isUnpublishedDraft && fg('confluence_new_reactions_entry_point_changes'))) && (
						<LivePagesByLineReactions
							contentId={contentId}
							spaceId={spaceId || ''}
							isDisabled={false}
							isContentCreationPanelOpen
							isSubtle
						/>
					)}
				</>
			);

			if (isLivePage && fg('confluence_frontend_single_line_byline_live_pages')) {
				return (
					<LivePagesBylineStyledMainDivSingleLine>
						<Flex
							alignItems="center"
							testId="live-pages-byline-single-line"
							ref={bylineRef}
							xcss={[
								textContainerStyles,
								singleLineBaseStyles,
								showAllApps && singleLineWrapStyles,
							]}
							gap="space.050"
						>
							{!fg('cc_page_experiences_byline_badge_style') ? (
								<Flex alignItems="center" gap="space.100">
									<ProfileAvatar
										src={pageOwnerOrFirstContributorData.src}
										userId={pageOwnerOrFirstContributorData.userId}
										isAnonymous={pageOwnerOrFirstContributorData.isAnonymous}
										name={pageOwnerOrFirstContributorData.name}
										size="small"
										testId="live-pages-byline-owner-or-first-contributor-avatar"
									/>
									<StyledAuthorDiv>{CreatorOrOwnerByline}</StyledAuthorDiv>
								</Flex>
							) : (
								<BadgeStylePageOwnershipByline owner={owner} author={author} />
							)}
							<>
								{ByLineCommonContent}
								{!isExternalShare && hasApps && (
									<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
										<LazyLivePagesByLineApps
											contentId={contentId}
											connectItems={connectItems}
											forgeItems={forgeItems}
											forgeError={forgeError}
											showLivePagesExtensionPoints
											isSingleLineByline
											toggleShowAllApps={toggleShowAllApps}
											showLessAppsButton={showAllApps || isOverflowing}
											showLessRef={showLessButtonRef}
										/>
									</ErrorBoundary>
								)}
							</>
						</Flex>
						{!showAllApps && hasApps && isOverflowing && (
							<Box xcss={toggleButtonGradientStyles}>
								<IconButton
									appearance="subtle"
									icon={ChevronRightIcon}
									label={intl.formatMessage(i18n.showAllApps)}
									onClick={toggleShowAllApps}
									testId="show-all-apps-button"
								/>
							</Box>
						)}
						<WidthObserver setWidth={updateContentAreaWidth} />
					</LivePagesBylineStyledMainDivSingleLine>
				);
			}

			return (
				<Flex direction="column" testId="live-pages-byline-multi-line">
					<LivePagesBylineStyledMainDiv>
						{!fg('cc_page_experiences_byline_badge_style') && (
							<StyledAvatars>
								<ProfileAvatar
									src={pageOwnerOrFirstContributorData.src}
									userId={pageOwnerOrFirstContributorData.userId}
									isAnonymous={pageOwnerOrFirstContributorData.isAnonymous}
									name={pageOwnerOrFirstContributorData.name}
									size="small"
									testId="live-pages-byline-owner-or-first-contributor-avatar"
								/>
							</StyledAvatars>
						)}
						<BylineStyledTextDiv
							data-testId="byline-text-component"
							hasBadgeStylePageOwnerBylineItem={fg('cc_page_experiences_byline_badge_style')}
						>
							<Flex gap="space.050" justifyContent="center" alignItems="center">
								{!fg('cc_page_experiences_byline_badge_style') ? (
									<StyledAuthorDiv>{CreatorOrOwnerByline}</StyledAuthorDiv>
								) : (
									<BadgeStylePageOwnershipByline owner={owner} author={author} />
								)}
								{ByLineCommonContent}
							</Flex>
						</BylineStyledTextDiv>
					</LivePagesBylineStyledMainDiv>
					{!isExternalShare &&
						hasApps &&
						fg('confluence_frontend_extension_points_in_live_pages') && (
							<Flex alignItems="center" xcss={bylineAppsStyles}>
								<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
									<LazyLivePagesByLineApps
										contentId={contentId}
										connectItems={connectItems}
										forgeItems={forgeItems}
										forgeError={forgeError}
										showLivePagesExtensionPoints
									/>
								</ErrorBoundary>
							</Flex>
						)}
				</Flex>
			);
		},
	);
